"use client"

import { Table, TableBody } from "@/components/ui/table"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination"
import { Search, AlertCircle } from "lucide-react"
import { CategorySummary } from "@/components/ui/custom/applicantsTable/category-summary"
import { NoteHelper } from "@/components/ui/custom/applicantsTable/note-helper"
import { TableControls } from "@/components/ui/custom/applicantsTable/table-controls"
import { ApplicantsTableHeader } from "@/components/ui/custom/applicantsTable/table-header"
import { ApplicantRow } from "@/components/ui/custom/applicantsTable/table-row"
import { LoadingScreen } from "@/components/ui/fancy-loader"
import { ITEMS_PER_PAGE, CANDIDATE_CATEGORIES } from "@/utils/applicantsTable/constants"
import { generateCSV, downloadCSV } from "@/utils/applicantsTable/csv-export"
import { ApplicantsTableProps } from "@/utils/applicantsTable/customTypes"
import { processApplicants, filterApplicants, sortApplicants, paginateApplicants, getCategoryCounts } from "@/utils/applicantsTable/data-processing"
import { useTableState, useNoteState, useHiddenState, useCaseAction } from "@/utils/applicantsTable/hooks"
import { useGetHiringCampaignsIdApplicants } from '../../../openapi/api/endpoints/default/default';

export const ApplicantsTable = ({ hiringCampaignId }: ApplicantsTableProps) => {
  const { data, isLoading } = useGetHiringCampaignsIdApplicants(hiringCampaignId)
  const {
    searchTerm,
    setSearchTerm,
    showOnlyQualified,
    setShowOnlyQualified,
    showHidden,
    setShowHidden,
    currentPage,
    setCurrentPage,
    categoryFilter,
    setCategoryFilter,
    showNoteHelper,
    setShowNoteHelper,
    handleSort,
  } = useTableState()

  const {mutateAsync:caseAction}=useCaseAction()

  const onNoteUpdate=async (userId: string, note: string)=>{
    try{
      await caseAction({data:{
        action:'updateNote',
        caseId:userId,
        note:note??'',
        hiringCampaignId
      }})
    }catch(err){
      console.log('Failed to update note',{updateNoteErr:(err as Error).message})
    }
  }

  const onCandidateHide=async(userId: string, isHidden: boolean)=>{
      try{
      await caseAction({data:{
        action:'setVisibility',
        caseId:userId,
        isInvisible:isHidden,
        hiringCampaignId
      }})
    }catch(err){
      console.log('Failed to update note',{updateNoteErr:(err as Error).message})
    }
  }

  const { editingNote, localNotes, form, handleEditNote, handleSaveNote, handleCancelNote } = useNoteState()

  const { localHiddenStates, handleToggleHidden } = useHiddenState()

  if (isLoading) {
    return <LoadingScreen message="Loading applicants..." />
  }

  if (!data) {
    return <div className="text-center p-8 text-gray-500">No applicant data available</div>
  }

  // Process and filter data
  const processedApplicants = processApplicants(data, localNotes, localHiddenStates)
  const filteredApplicants = filterApplicants(
    processedApplicants,
    searchTerm,
    showOnlyQualified,
    showHidden,
    categoryFilter,
  )
  const sortedApplicants = sortApplicants(filteredApplicants, "category", "asc")
  const totalPages = Math.ceil(sortedApplicants.length / ITEMS_PER_PAGE)
  const paginatedApplicants = paginateApplicants(sortedApplicants, currentPage, ITEMS_PER_PAGE)

  // Calculate counts
  const categoryCounts = getCategoryCounts(processedApplicants, showHidden)
  const hiddenCount = processedApplicants.filter((applicant) => applicant.isHidden).length

  // Event handlers
  const handleDownloadCSV = () => {
    const csvContent = generateCSV(filteredApplicants, showOnlyQualified, hiringCampaignId)
    downloadCSV(csvContent)
  }

  const handleNoteUpdate = (userId: string) => {
    handleSaveNote(userId, onNoteUpdate)
  }

  const handleCandidateHide = (userId: string, currentHiddenState: boolean) => {
    handleToggleHidden(userId, currentHiddenState, onCandidateHide)
  }

  return (
    <div className="max-w-[2000px] mx-auto bg-white rounded-lg shadow-lg p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-4 md:mb-0">Applicants</h2>
        <TableControls
          showOnlyQualified={showOnlyQualified}
          onShowOnlyQualifiedChange={(checked) => {
            setShowOnlyQualified(checked)
            setCurrentPage(1)
          }}
          showHidden={showHidden}
          onShowHiddenChange={(checked) => {
            setShowHidden(checked)
            setCurrentPage(1)
          }}
          hiddenCount={hiddenCount}
          categoryFilter={categoryFilter}
          onCategoryFilterChange={setCategoryFilter}
          categoryCounts={categoryCounts}
          onDownloadCSV={handleDownloadCSV}
        />
      </div>

      <CategorySummary
        categoryCounts={categoryCounts}
        categoryFilter={categoryFilter}
        onCategoryFilterChange={setCategoryFilter}
      />

      <NoteHelper show={showNoteHelper} onDismiss={() => setShowNoteHelper(false)} />

      {/* Search */}
      <div className="relative w-full mb-6">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        <Input
          type="text"
          placeholder="Search applicants by name, email, or any field..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value)
            setCurrentPage(1)
          }}
          className="pl-10 w-full border-gray-300 focus:border-gray-500 focus:ring-gray-500"
        />
      </div>

      {/* Active filters display */}
      {(categoryFilter || showOnlyQualified || showHidden) && (
        <div className="mb-4 flex items-center flex-wrap gap-2">
          <span className="text-sm text-gray-500 mr-2">Active filters:</span>
          {categoryFilter && (
            <Badge
              className={CANDIDATE_CATEGORIES[categoryFilter].badge + " cursor-pointer"}
              onClick={() => setCategoryFilter(null)}
            >
              {categoryFilter} ×
            </Badge>
          )}
          {showOnlyQualified && (
            <Badge className="bg-blue-600 text-white cursor-pointer" onClick={() => setShowOnlyQualified(false)}>
              Qualified only ×
            </Badge>
          )}
          {showHidden && (
            <Badge className="bg-orange-600 text-white cursor-pointer" onClick={() => setShowHidden(false)}>
              Show hidden ×
            </Badge>
          )}
        </div>
      )}

      {/* Table */}
      <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm w-full">
        <Table>
          <ApplicantsTableHeader onSort={handleSort} />
          <TableBody>
            {paginatedApplicants.map((applicant) => (
              <ApplicantRow
                key={applicant._id}
                applicant={applicant}
                hiringCampaignId={hiringCampaignId}
                isEditing={editingNote === applicant._id}
                form={form}
                onEditNote={handleEditNote}
                onSaveNote={handleNoteUpdate}
                onCancelNote={handleCancelNote}
                onToggleHidden={handleCandidateHide}
              />
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Empty state */}
      {filteredApplicants.length === 0 && (
        <div className="text-center p-8 text-gray-500 bg-white rounded-lg border border-gray-200 mt-4">
          <AlertCircle className="mx-auto h-12 w-12 mb-4 text-gray-400" />
          <p className="text-lg font-medium">No applicants found</p>
          <p className="mt-1">Try adjusting your search or filters</p>
        </div>
      )}

      {/* Pagination */}
      {filteredApplicants.length > 0 && (
        <div className="mt-6">
          <div className="text-sm text-gray-500 mb-2">
            Showing {paginatedApplicants.length} of {filteredApplicants.length} applicants
            {hiddenCount > 0 && !showHidden && ` (${hiddenCount} hidden)`}
          </div>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }}
                  className={currentPage === 1 ? "pointer-events-none opacity-50" : ""}
                />
              </PaginationItem>
              {[...Array(totalPages)].map((_, i) => (
                <PaginationItem key={i}>
                  <PaginationLink
                    href="#"
                    onClick={(e) => {
                      e.preventDefault()
                      setCurrentPage(i + 1)
                    }}
                    isActive={currentPage === i + 1}
                    className={currentPage === i + 1 ? "bg-gray-800 text-white" : ""}
                  >
                    {i + 1}
                  </PaginationLink>
                </PaginationItem>
              ))}
              <PaginationItem>
                <PaginationNext
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }}
                  className={currentPage === totalPages ? "pointer-events-none opacity-50" : ""}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  )
}
